import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import requestAuditService from "../../services/requestAuditService";

const WorkflowApproval = () => {
  const [searchParams] = useSearchParams();
  const emailToken = searchParams.get("token");

  // API base URL and API Key
  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
  const API_BASE = (API_ENDPOINT || "").replace(/\/+$/, "");
  const EMAIL_ACTION_DETAILS_URL = `${API_BASE}/sso/email-actions/details`;
  const EMAIL_ACTION_EXECUTE_URL = `${API_BASE}/sso/email-actions/execute`;
  const API_KEY = "dev-key-12345";

  // Get user token from localStorage
  const getUserToken = () => {
    try {
      const tokenStorage = JSON.parse(localStorage.getItem("okta-token-storage") || "{}");
      return tokenStorage?.accessToken?.accessToken || null;
    } catch {
      return null;
    }
  };

  // Get username from token
  const getUsernameFromToken = () => {
    try {
      const tokenStorage = JSON.parse(localStorage.getItem("okta-token-storage") || "{}");
      const claims = tokenStorage?.idToken?.claims || tokenStorage?.accessToken?.claims || {};
      return claims.preferred_username || claims.email || claims.sub || claims.gpid || "";
    } catch {
      return "";
    }
  };

  // State
  const [phase, setPhase] = useState("loading");
  const [tokenDetails, setTokenDetails] = useState(null);
  const [username, setUsername] = useState(getUsernameFromToken());
  const [identityInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ text: "", isError: false });
  const [result, setResult] = useState({ title: "", message: "", color: "", icon: "" });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isViewMoreOpen, setIsViewMoreOpen] = useState(false);
  const [ssoLogoUrl, setSsoLogoUrl] = useState(null);
  const [ssoCertificateUrl, setSsoCertificateUrl] = useState(null);
  const [ssoCertificateName, setSsoCertificateName] = useState(null);
  const [logoPreviewSrc, setLogoPreviewSrc] = useState(null);

  function camelToTitle(str) {
    return str
      .split(/(?=[A-Z])/) // split before uppercase letters
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  const showResult = useCallback((title, message, color, icon) => {
    setResult({ title, message, color, icon });
    setPhase("result");
  }, []);

  const extractDisplayMessage = useCallback((payloadMessage, fallbackMessage) => {
    if (!payloadMessage || typeof payloadMessage !== "string") {
      return fallbackMessage;
    }

    const directParsed = parseJsonSafely(payloadMessage);
    if (directParsed?.message) {
      return directParsed.message;
    }

    const firstBrace = payloadMessage.indexOf("{");
    const lastBrace = payloadMessage.lastIndexOf("}");
    if (firstBrace !== -1 && lastBrace > firstBrace) {
      const embeddedJson = payloadMessage.slice(firstBrace, lastBrace + 1);
      const candidates = [embeddedJson, embeddedJson.replace(/\\(?=")/g, "")];
      for (const candidate of candidates) {
        const parsed = parseJsonSafely(candidate);
        if (parsed?.message) {
          return parsed.message;
        }
      }
    }

    const regexMatch = payloadMessage.match(/"message"\s*:\s*"((?:\\"|[^"])*)"/);
    if (regexMatch?.[1]) {
      return regexMatch[1].replace(/\\(?=")/g, "");
    }

    return payloadMessage
      .replace(/^Failed to execute email action:\s*/i, "")
      .trim() || fallbackMessage;
  }, []);

  // Initialize
  useEffect(() => {
    const init = async () => {
      if (!emailToken) {
        showResult("Invalid Link", "No approval token found in the URL.", "#ea4335", "❌");
        return;
      }

      // Check if user is authenticated first
      const userToken = getUserToken();
      const currentUsername = getUsernameFromToken();

      if (!userToken || !currentUsername) {
        // User not authenticated - save token and redirect to login
        sessionStorage.setItem('wf_email_token', emailToken);
        window.location.href = '/login';
        return;
      }

      try {
        // Fetch token details with required headers
        const headers = {
          'accept': '*/*',
          'X-API-Key': API_KEY,
          'Authorization': `Bearer ${userToken}`
        };

        const detailsResp = await axios.get(EMAIL_ACTION_DETAILS_URL, {
          params: { token: emailToken },
          headers: headers
        });
        console.log('Token details response:', detailsResp.data);

        const detailsPayload = detailsResp?.data;
        if (!detailsPayload || detailsPayload?.success === false || detailsPayload?.status === "Failed") {
          showResult(
            "Action Failed",
            detailsPayload?.message || "This link has expired or has already been used.",
            "#ea4335",
            "❌"
          );
          return;
        }

        const normalizedDetails = {
          ...(detailsPayload?.tokenDetails || {}),
          ...(detailsPayload?.requestData || {}),
          ...(detailsPayload?.requestData?.displayFields || {}),
          ...(detailsPayload?.requestData?.rawRequest || {}),
          ...(detailsPayload?.requestData?.rawRequest?.request || {}),
          rawIntegrationType: detailsPayload?.requestData?.rawRequest?.type,
          requestPayloadType: detailsPayload?.requestData?.rawRequest?.request?.type,
          responseSuccess: detailsPayload?.success,
          tokenDetails: detailsPayload?.tokenDetails,
          requestData: detailsPayload?.requestData,
        };

        setTokenDetails(normalizedDetails);
        setUsername(currentUsername);
        setIsAuthenticated(true);
        setPhase("auth");
      } catch (err) {
        console.error("Init error:", err);
        const errMsg = err.response?.data?.message || "Failed to load approval details. Please try again.";
        showResult("Error", errMsg, "#ea4335", "❌");
      }
    };

    init();
  }, [emailToken, showResult, EMAIL_ACTION_DETAILS_URL]);

  // Execute action - using fetch to bypass axios interceptor
  const executeAction = async (action) => {
    setIsLoading(true);
    setStatusMsg({ text: "", isError: false });

    try {
      const userToken = getUserToken();
      const headers = {
        "Content-Type": "application/x-www-form-urlencoded",
        "accept": "*/*",
        "X-API-Key": API_KEY
      };
      if (userToken) {
        headers["Authorization"] = `Bearer ${userToken}`;
      }

      // Build form data
      const formData = new URLSearchParams();
      if (!isAuthenticated && identityInput.trim()) {
        formData.append("identity", identityInput.trim());
      }

      let executeUrl = `${EMAIL_ACTION_EXECUTE_URL}?token=${encodeURIComponent(emailToken)}`;
      if (action === "REJECT" && rejectionReason.trim()) {
        executeUrl += `&comments=${encodeURIComponent(rejectionReason.trim())}`;
      }
      console.log('Execute request:', { url: executeUrl, body: formData.toString(), headers });
      
      // Using fetch instead of axios to bypass interceptor redirect
      const resp = await fetch(executeUrl, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      const resultData = await resp.json();
      console.log('Execute response:', { status: resp.status, data: resultData });

      if (resp.ok && resultData.success) {
        if (action === "APPROVE") {
          showResult(
            "Approved Successfully!",
            resultData.message || "You have approved this request. You may close this page.",
            "#34a853",
            "✅"
          );
        } else {
          showResult(
            "Request Rejected",
            resultData.message || "You have rejected this request. You may close this page.",
            "#ea4335",
            "❌"
          );
        }
      } else if (resp.status === 401 || resp.status === 403) {
        console.error("Auth error:", resp.status, resultData);
        setStatusMsg({
          text: extractDisplayMessage(resultData?.message, "Authentication required or unauthorized. Please ensure you are logged in."),
          isError: true
        });
      } else {
        setStatusMsg({
          text: extractDisplayMessage(resultData?.message, "Action failed. Please try again."),
          isError: true
        });
      }
    } catch (err) {
      console.error("Execute error:", err);
      setStatusMsg({ text: "Network error. Please check your connection and try again.", isError: true });
    } finally {
      setIsLoading(false);
    }
  };

  const normalizeLookupKey = (key = "") => key.toString().toLowerCase().replace(/[\s_-]/g, "");

  const parseJsonSafely = (value) => {
    if (typeof value !== "string") {
      return null;
    }
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const getTokenValidationStatusText = (status) => {
    const normalized = (status || "").toString().toUpperCase();
    if (["SUCCESS", "PASSED", "PASS"].includes(normalized)) {
      return "Success";
    }
    if (["ERROR", "FAIL", "FAILED"].includes(normalized)) {
      return "Failed";
    }
    return normalized ? normalized.charAt(0) + normalized.slice(1).toLowerCase() : "Unknown";
  };

  const getTokenValidationStatusColor = (status) => {
    const normalized = (status || "").toString().toUpperCase();
    if (["SUCCESS", "PASSED", "PASS"].includes(normalized)) {
      return "success";
    }
    if (["ERROR", "FAIL", "FAILED"].includes(normalized)) {
      return "error";
    }
    return "warning";
  };

  const getAutomatedDecisionFromTokenStatus = (tokenValidation) => {
    const status = (tokenValidation?.status || "").toString().toUpperCase();
    if (["SUCCESS", "PASSED", "PASS"].includes(status)) {
      return "Approve";
    }
    if (["ERROR", "FAIL", "FAILED"].includes(status)) {
      return "Reject";
    }
    return "Manual Review";
  };

  const getAutomatedDecisionColor = (tokenValidation) => {
    const decision = getAutomatedDecisionFromTokenStatus(tokenValidation);
    if (decision === "Approve") {
      return "success";
    }
    if (decision === "Reject") {
      return "error";
    }
    return "warning";
  };

  const detailsData = useMemo(() => {
    if (!tokenDetails || typeof tokenDetails !== "object") {
      return {};
    }

    const merged = { ...tokenDetails };
    const nestedKeys = [
      "requestData",
      "requestDetails",
      "payload",
      "data",
      "details",
      "workflowData",
      "auditApprovalData",
      "meta",
    ];

    nestedKeys.forEach((key) => {
      const nested = tokenDetails[key];
      if (nested && typeof nested === "object" && !Array.isArray(nested)) {
        Object.assign(merged, nested);
      }
      const parsed = parseJsonSafely(nested);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        Object.assign(merged, parsed);
      }
    });

    return merged;
  }, [tokenDetails]);

  const normalizedDetailsMap = useMemo(() => {
    const mapped = {};
    Object.entries(detailsData || {}).forEach(([key, value]) => {
      mapped[normalizeLookupKey(key)] = value;
    });
    return mapped;
  }, [detailsData]);

  const getFieldValue = useCallback(
    (...aliases) => {
      for (const alias of aliases) {
        const normalizedAlias = normalizeLookupKey(alias);
        if (normalizedAlias in normalizedDetailsMap) {
          const value = normalizedDetailsMap[normalizedAlias];
          if (value !== undefined && value !== null && value !== "") {
            return value;
          }
        }
      }
      return undefined;
    },
    [normalizedDetailsMap]
  );

  const hasValue = (value) => value !== undefined && value !== null && value !== "";

  const isYesValue = (value) => {
    if (typeof value === "boolean") {
      return value;
    }
    const normalized = String(value || "").toLowerCase();
    return ["yes", "true", "1", "y"].includes(normalized);
  };

  const formatFieldValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    if (value && typeof value === "object") {
      return JSON.stringify(value);
    }
    return value;
  };

  const getStatusPillStyles = (type) => {
    if (type === "success") {
      return { backgroundColor: "#e9f7ee", color: "#1e8e3e", border: "1px solid #cbead6" };
    }
    if (type === "error") {
      return { backgroundColor: "#fdeceb", color: "#c62828", border: "1px solid #f7cecb" };
    }
    return { backgroundColor: "#fff5e6", color: "#8a5a00", border: "1px solid #ffe0b2" };
  };

  // Styles
  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: "#f5f7fa",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px"
    },
    verifiedBadge: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "16px",
      padding: "8px 16px",
      background: "#fff",
      borderRadius: "8px",
      border: "1px solid #e0e0e0",
      width: "fit-content"
    },
    verifiedDot: {
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      background: "#34a853"
    },
    verifiedText: {
      fontSize: "14px",
      color: "#333",
      fontWeight: "500"
    },
    verifiedEmail: {
      marginLeft: "auto",
      padding: "6px 12px",
      background: "#f5f7fa",
      borderRadius: "4px",
      fontSize: "13px",
      color: "#666",
      border: "1px solid #e0e0e0"
    },
    card: {
      background: "#fff",
      borderRadius: "8px",
      padding: "24px",
      maxWidth: "760px",
      width: "100%",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      border: "1px solid #e0e0e0"
    },
    cardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
      paddingBottom: "16px",
      borderBottom: "1px solid #f0f0f0"
    },
    cardTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#333",
      margin: 0
    },
    statusBadge: {
      padding: "4px 12px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: "500"
    },
    statusPending: {
      background: "#e3f2fd",
      color: "#1976d2"
    },
    statusApproved: {
      background: "#e8f5e9",
      color: "#2e7d32"
    },
    statusRejected: {
      background: "#ffebee",
      color: "#c62828"
    },
    subtitle: {
      color: "#666",
      fontSize: "13px",
      marginBottom: "20px",
      lineHeight: "1.5"
    },
    reqTitle: {
      fontSize: "24px",
      fontWeight: 700,
      color: "#1f2937",
      margin: 0
    },
    section: {
      marginTop: "14px",
      marginBottom: "8px"
    },
    sectionTitle: {
      fontSize: "14px",
      fontWeight: 700,
      color: "#111827",
      marginBottom: "10px"
    },
    detailsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
      columnGap: "20px",
      rowGap: "14px"
    },
    detailsItem: {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      minWidth: 0
    },
    detailsItemLabel: {
      fontSize: "12px",
      color: "#4b5563",
      fontWeight: 500
    },
    detailsItemValue: {
      fontSize: "13px",
      color: "#111827",
      fontWeight: 600,
      wordBreak: "break-word"
    },
    sectionDivider: {
      borderTop: "1px solid #e5e7eb",
      margin: "14px 0"
    },
    tokenPanel: {
      marginTop: "10px",
      padding: "12px",
      borderRadius: "8px",
      background: "#f7f9fc",
      border: "1px solid #d8e2f1"
    },
    tokenBadge: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "4px",
      padding: "2px 8px",
      minHeight: "22px",
      fontSize: "12px",
      fontWeight: 700
    },
    tokenRow: {
      display: "flex",
      gap: "8px",
      alignItems: "center",
      flexWrap: "wrap",
      marginBottom: "8px"
    },
    tokenMsg: {
      color: "#616161",
      fontSize: "13px",
      marginBottom: "8px"
    },
    tokenTableWrap: {
      overflowX: "auto",
      border: "1px solid #d8e2f1",
      borderRadius: "8px",
      backgroundColor: "#fff",
      marginBottom: "10px"
    },
    tokenTable: {
      width: "100%",
      borderCollapse: "collapse",
      tableLayout: "fixed"
    },
    tokenTableHead: {
      textAlign: "left",
      padding: "10px 12px",
      borderBottom: "1px solid #d8e2f1",
      fontSize: "12px",
      fontWeight: 700,
      color: "#4b4b4b",
      backgroundColor: "#eef4fd"
    },
    tokenTableCell: {
      padding: "10px 12px",
      borderBottom: "1px solid #e9eef8",
      fontSize: "13px",
      color: "#4b4b4b"
    },
    detailBox: {
      marginBottom: "20px"
    },
    detailRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "10px 0",
      fontSize: "14px",
      borderBottom: "1px solid #f5f5f5"
    },
    detailRowLast: {
      borderBottom: "none"
    },
    detailLabel: {
      color: "#666",
      fontWeight: "400"
    },
    detailValue: {
      color: "#333",
      fontWeight: "500"
    },
    detailValueApprove: {
      color: "#2e7d32",
      fontWeight: "600"
    },
    detailValueReject: {
      color: "#c62828",
      fontWeight: "600"
    },
    reasonSection: {
      marginBottom: "20px"
    },
    reasonLabel: {
      fontSize: "13px",
      fontWeight: "500",
      color: "#333",
      marginBottom: "8px",
      display: "block"
    },
    reasonTextarea: {
      width: "100%",
      padding: "12px",
      border: "1px solid #e0e0e0",
      borderRadius: "4px",
      fontSize: "14px",
      outline: "none",
      resize: "vertical",
      minHeight: "80px",
      boxSizing: "border-box",
      fontFamily: "inherit"
    },
    btnGroup: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end"
    },
    actionRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      marginTop: "8px"
    },
    viewMoreBtn: {
      background: "transparent",
      color: "#0b5fc6",
      border: "none",
      padding: 0,
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      textDecoration: "underline"
    },
    btn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "10px 20px",
      border: "none",
      borderRadius: "4px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer"
    },
    btnCancel: {
      background: "#fff",
      color: "#333",
      border: "1px solid #e0e0e0"
    },
    btnApprove: {
      background: "#0066cc",
      color: "#fff"
    },
    btnReject: {
      background: "#c62828",
      color: "#fff"
    },
    spinner: {
      margin: "20px auto",
      width: "24px",
      height: "24px",
      border: "2px solid #e0e0e0",
      borderTop: "2px solid #0066cc",
      borderRadius: "50%",
      animation: "spin 0.8s linear infinite"
    },
    statusMsg: {
      marginTop: "16px",
      fontSize: "13px",
      padding: "12px",
      borderRadius: "4px"
    },
    statusError: {
      background: "#ffebee",
      color: "#c62828",
      border: "1px solid #ef9a9a"
    },
    securityNote: {
      marginTop: "16px",
      fontSize: "11px",
      color: "#999",
      lineHeight: "1.4",
      display: "flex",
      alignItems: "flex-start",
      gap: "6px"
    },
    closeHint: {
      marginTop: "24px",
      color: "#999",
      fontSize: "13px",
      textAlign: "center"
    },
    icon: {
      fontSize: "48px",
      marginBottom: "12px",
      textAlign: "center"
    },
    title: {
      color: "#333",
      fontSize: "22px",
      marginBottom: "8px",
      textAlign: "center"
    },
    modalOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.35)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "16px"
    },
    modalCard: {
      width: "min(920px, 100%)",
      maxHeight: "85vh",
      overflow: "hidden",
      background: "#fff",
      borderRadius: "10px",
      border: "1px solid #e5e7eb",
      boxShadow: "0 10px 25px rgba(0,0,0,0.15)"
    },
    modalHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 20px",
      borderBottom: "1px solid #e5e7eb"
    },
    modalClose: {
      border: "none",
      background: "transparent",
      color: "#4b5563",
      fontSize: "20px",
      lineHeight: 1,
      cursor: "pointer",
      padding: "2px 6px"
    },
    modalBody: {
      padding: "16px 20px 20px",
      overflowY: "auto",
      maxHeight: "calc(85vh - 64px)"
    }
  };

  // Determine if this is an approval or rejection action
  const isRejectAction = (tokenDetails?.action || tokenDetails?.tokenDetails?.action) === "REJECT";
  const requestNumber =
    getFieldValue("requestNumber", "request", "requestId", "req", "request_no") ||
    getFieldValue("businessKey") ||
    tokenDetails?.requestNumber ||
    tokenDetails?.businessKey ||
    tokenDetails?.requestId ||
    "-";
  const expiryText = getFieldValue("expiresIn", "expiry", "expiration", "expiresAt", "expiryIn");
  const tokenValidation = detailsData?.tokenValidation || tokenDetails?.tokenValidation;
  const integrationTypeValue = getFieldValue("Integration Type", "integrationType", "type") || "";
  const integrationTypeUpper = `${integrationTypeValue}`.toUpperCase();
  const rawIntegrationTypeUpper = `${getFieldValue("rawIntegrationType") || ""}`.toUpperCase().trim();
  const requestPayloadTypeUpper = `${getFieldValue("requestPayloadType") || ""}`.toUpperCase().trim();
  const m2mAccessTypeValue = getFieldValue(
    "Application/Services Type",
    "Application Services Type",
    "Application Service Type",
    "Access Type",
    "accessType",
    "accessTypeName",
    "applicationServicesType",
    "applicationServiceType",
    "serviceType",
    "appServiceType"
  );
  const m2mCustomScopeNameValue = getFieldValue(
    "Custom Scope Name",
    "Custom Scope Names",
    "customScopeName",
    "customScopeNames",
    "scopeName",
    "scopeNames",
    "Scopes",
    "scopes"
  );
  const hasAccessTypeValue = hasValue(m2mAccessTypeValue);
  const hasScopesValue = hasValue(m2mCustomScopeNameValue);
  const isSamlFlow = integrationTypeUpper.includes("SAML");
  const isM2MFlow =
    integrationTypeUpper.includes("M2M") ||
    integrationTypeUpper.includes("MACHINE2MACHINE") ||
    integrationTypeUpper.includes("MACHINE 2 MACHINE") ||
    rawIntegrationTypeUpper === "M2M" ||
    requestPayloadTypeUpper.includes("SERVICE_APPLICATION") ||
    hasAccessTypeValue ||
    hasScopesValue;
  const isOidcFlow = integrationTypeUpper.includes("OIDC") && !isM2MFlow;
  const hasLogoRawValue = getFieldValue("hasLogo", "Has Logo");
  const hasCertificateRawValue = getFieldValue("hasCertificate", "hasSigningCertificate", "Has Certificate", "Has Signing Certificate");
  const hasLogo = hasValue(hasLogoRawValue) && isYesValue(hasLogoRawValue);
  const hasCertificate = hasValue(hasCertificateRawValue) && isYesValue(hasCertificateRawValue);
  const fallbackLogoUrl = getFieldValue("logoUrl", "Logo URL", "applicationLogoUrl", "logo");
  const fallbackCertificateUrl = getFieldValue("certificateUrl", "Certificate URL", "signingCertificateUrl");
  const certificateFileName =
    ssoCertificateName ||
    getFieldValue("certificateFileName", "Certificate File Name", "certFileName") ||
    "Certificate.crt";

  useEffect(() => {
    let isMounted = true;

    const clearBlobUrls = () => {
      setSsoLogoUrl((prev) => {
        if (prev && prev.startsWith("blob:")) {
          URL.revokeObjectURL(prev);
        }
        return null;
      });
      setSsoCertificateUrl((prev) => {
        if (prev && prev.startsWith("blob:")) {
          URL.revokeObjectURL(prev);
        }
        return null;
      });
      setSsoCertificateName(null);
    };

    const fetchAttachments = async () => {
      const normalizedRequestNumber = String(requestNumber || "").trim();
      if (!normalizedRequestNumber || normalizedRequestNumber === "-") {
        clearBlobUrls();
        return;
      }

      if (!hasLogo && !hasCertificate) {
        clearBlobUrls();
        return;
      }

      clearBlobUrls();

      if (hasLogo) {
        try {
          const logoResponse = await requestAuditService.getSSOAttachment(normalizedRequestNumber, "LOGO");
          if (isMounted && logoResponse?.data) {
            const blob = new Blob([logoResponse.data], { type: logoResponse.headers["content-type"] || "image/png" });
            setSsoLogoUrl(URL.createObjectURL(blob));
          }
        } catch (error) {
          console.error("Error fetching SSO logo:", error);
        }
      }

      if (hasCertificate) {
        try {
          const certResponse = await requestAuditService.getSSOAttachment(normalizedRequestNumber, "CERTIFICATE");
          if (isMounted && certResponse?.data) {
            const blob = new Blob([certResponse.data], { type: certResponse.headers["content-type"] || "application/x-x509-ca-cert" });
            setSsoCertificateUrl(URL.createObjectURL(blob));
            const contentDisposition = certResponse.headers["content-disposition"];
            if (contentDisposition) {
              const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (filenameMatch && filenameMatch[1]) {
                setSsoCertificateName(filenameMatch[1].replace(/['"]/g, ""));
              }
            }
          }
        } catch (error) {
          console.error("Error fetching SSO certificate:", error);
        }
      }
    };

    fetchAttachments();

    return () => {
      isMounted = false;
    };
  }, [requestNumber, hasLogo, hasCertificate]);

  const businessInfoFields = [
    { label: "Integration Type", value: getFieldValue("Integration Type", "integrationType", "type") },
    { label: "Sector", value: getFieldValue("sector") },
    { label: "Environment", value: getFieldValue("environment") },
    { label: "Business Application", value: getFieldValue("businessApplication", "businessApp", "businessAppName", "business_application") },
    { label: "CGF", value: getFieldValue("cgf") },
    { label: "Data Classification", value: getFieldValue("dataClassification", "classification", "data_classification") },
    { label: "Application Owner", value: getFieldValue("applicationOwner", "appOwner", "owner", "application_owner") },
    { label: "Application Service", value: getFieldValue("applicationService", "appService", "service", "application_service") },
  ].filter((item) => item.value !== undefined && item.value !== null && item.value !== "");

  const applicationInfoFields = [
    { label: "Application Name", value: getFieldValue("applicationName", "appName", "application_name") },
    { label: "Name ID Format", value: getFieldValue("nameIdFormat", "nameIDFormat", "name_id_format") },
    { label: "URL", value: getFieldValue("url", "applicationUrl", "applicationURL", "application_url") },
    { label: "Email Address", value: getFieldValue("emailAddress", "email", "applicationEmail", "email_address") },
    { label: "End-user Application URL", value: getFieldValue("endUserApplicationUrl", "endUserUrl", "end_user_application_url") },
    { label: "Audience URI (SP Entity ID)", value: getFieldValue("audienceUri", "audienceUriSpEntityId", "spEntityId", "audience_uri") },
    { label: "Sample ID", value: getFieldValue("sampleId", "sampleID", "sample_id") },
  ].filter((item) => item.value !== undefined && item.value !== null && item.value !== "");

  const renderModalField = (label, value, options = {}) => {
    if (!hasValue(value)) {
      return null;
    }
    return (
      <div key={label} style={styles.detailsItem}>
        <span style={styles.detailsItemLabel}>{label}</span>
        <span style={{ ...styles.detailsItemValue, ...(options.wordBreak ? { wordBreak: "break-all" } : {}) }}>
          {formatFieldValue(value)}
        </span>
      </div>
    );
  };

  // Render loading phase
  if (phase === "loading") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.icon}>⏳</div>
          <h1 style={styles.title}>Loading Request Details...</h1>
          <div style={styles.spinner}></div>
        </div>
      </div>
    );
  }

  // Render result phase
  if (phase === "result") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.icon}>{result.icon}</div>
          <h1 style={{ ...styles.title, color: result.color }}>{result.title}</h1>
          <p style={{ color: "#666", fontSize: "16px", lineHeight: "1.6", textAlign: "center" }}>{result.message}</p>
          <p style={styles.closeHint}>You can safely close this tab.</p>
        </div>
      </div>
    );
  }

  // Render auth phase
  return (
    <div style={styles.container}>
      {/* Verified Identity Badge */}
      {isAuthenticated && username && (
        <div style={styles.verifiedBadge}>
          <div style={styles.verifiedDot}></div>
          <span style={styles.verifiedText}>Verified Identity</span>
          <span style={styles.verifiedEmail}>{username}</span>
        </div>
      )}

      {/* Request Authorization Card */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.reqTitle}>Req # {requestNumber}</h2>
          {/* <span
            style={{
              ...styles.statusBadge,
              ...(isRejectAction ? styles.statusRejected : styles.statusPending),
            }}
          >
            {expiryText ? `Expires in ${formatFieldValue(expiryText)}` : (isRejectAction ? "Reject" : "Submit")}
          </span> */}
        </div>

        <p style={styles.subtitle}>Confirm the request details before submission.</p>

        {/* Request Summary */}
        {tokenDetails && (
          <div style={styles.detailBox}>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Request Type:</span>
              <span style={styles.detailValue}>{getFieldValue("requestType", "type") || tokenDetails.requestType || "SSO_ONBOARDING"}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Requestor ID:</span>
              <span style={styles.detailValue}>{getFieldValue("requestor", "requestorId", "requesterGpid", "requestorGpid", "initiatorGpid") || tokenDetails.requestor || tokenDetails.requestorId || "-"}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Action Status:</span>
              <span style={isRejectAction ? styles.detailValueReject : styles.detailValueApprove}>{isRejectAction ? "Reject" : "Approve"}</span>
            </div>
            <div style={{ ...styles.detailRow, ...styles.detailRowLast }}>
              <span style={styles.detailLabel}>Approver:</span>
              <span style={styles.detailValue}>{getFieldValue("maskedApprover", "approver") || tokenDetails.maskedApprover || tokenDetails.approver || "S1******"}</span>
            </div>
          </div>
        )}

        {/* Reason for Rejection (only for reject action) */}
        {isRejectAction && (
          <div style={styles.reasonSection}>
            <label style={styles.reasonLabel}>Reason for rejection*</label>
            <textarea
              style={styles.reasonTextarea}
              placeholder="Please explain the rejection"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
        )}

        {/* Actions */}
        <div style={styles.actionRow}>
          <button style={styles.viewMoreBtn} onClick={() => setIsViewMoreOpen(true)} disabled={isLoading}>
            View more details
          </button>

          <div style={styles.btnGroup}>
            <button style={{ ...styles.btn, ...styles.btnCancel }} onClick={() => window.close()} disabled={isLoading}>
              Cancel
            </button>
            {!isRejectAction && (
              <button
                style={{ ...styles.btn, ...styles.btnApprove, opacity: isLoading ? 0.6 : 1 }}
                onClick={() => executeAction("APPROVE")}
                disabled={isLoading}
              >
                ✓ Approve
              </button>
            )}
            {isRejectAction && (
              <button
                style={{ ...styles.btn, ...styles.btnReject, opacity: isLoading ? 0.6 : 1 }}
                onClick={() => executeAction("REJECT")}
                disabled={isLoading || !rejectionReason.trim()}
              >
                ✕ Reject
              </button>
            )}
          </div>
        </div>

        {/* Loading Spinner */}
        {isLoading && <div style={styles.spinner}></div>}

        {/* Status Message */}
        {statusMsg.text && <div style={{ ...styles.statusMsg, ...styles.statusError }}>{statusMsg.text}</div>}

        {/* Security Note */}
        <p style={styles.securityNote}>
          <span>⚠️</span>
          <span>This link is single-use and expires after 8 hours. It is bound to a specific approver. Forwarded links will be blocked.</span>
        </p>
      </div>

      {isViewMoreOpen && (
        <div style={styles.modalOverlay} onClick={() => setIsViewMoreOpen(false)}>
          <div style={styles.modalCard} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: "24px", fontWeight: 700, color: "#1f2937" }}>Req # {requestNumber}</h3>
              <button style={styles.modalClose} onClick={() => setIsViewMoreOpen(false)} aria-label="Close details modal">
                ×
              </button>
            </div>
            {
              (tokenDetails?.requestType === "ENTRA_APP_REGISTRATION" ||  tokenDetails?.typeCode === "ENTRA_APP_REGISTRATION" || tokenDetails?.requestType === "APPLICATION_RETIREMENT" ||  tokenDetails?.typeCode === "APPLICATION_RETIREMENT") ?
              <div style={styles.modalBody}>
                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Request Details</div>
                    <div style={styles.detailsGrid}>
                      {
                        Object.entries(tokenDetails)?.filter?.(([k]) => ['applicationName','environment','businessOwnerGpid','supportGroup','technicalOwnerGpids','signInAudience','apiPermissions','businessPurpose'].includes(k))
                        ?.map(([key,val])=>(
                          (key && (Array.isArray(val) ? val.length : val)) ? <div key={key} style={styles.detailsItem}>
                            <span style={styles.detailsItemLabel}>{key === "businessPurpose" ? "Remarks/Business Justification" : camelToTitle(key)}</span>
                            <span style={styles.detailsItemValue}>{key === "apiPermissions" ? val?.map?.((v)=> v?.name)?.join(", ") : formatFieldValue(val)}</span>
                          </div> : null
                        ))
                      }
                    </div>
                  </div>
              </div>
              :
              <div style={styles.modalBody}>
                {businessInfoFields.length > 0 && (
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>Business Information</div>
                    <div style={styles.detailsGrid}>
                      {businessInfoFields.map((field) => (
                        <div key={field.label} style={styles.detailsItem}>
                          <span style={styles.detailsItemLabel}>{field.label}</span>
                          <span style={styles.detailsItemValue}>{formatFieldValue(field.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(applicationInfoFields.length > 0 || isSamlFlow || isOidcFlow || isM2MFlow) && <div style={styles.sectionDivider}></div>}

                {(applicationInfoFields.length > 0 || isSamlFlow || isOidcFlow || isM2MFlow) && (
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>Application Details</div>
                    <div style={styles.detailsGrid}>
                      {renderModalField("Application Name", getFieldValue("Application Name", "applicationName", "appName", "application_name"))}

                      {isSamlFlow && renderModalField("End-user Application URL", getFieldValue("Application URL", "applicationURL", "applicationUrl", "endUserApplicationUrl"), { wordBreak: true })}
                      {isSamlFlow && renderModalField("Assertion Consumer Service (ACS) URL", getFieldValue("Assertion Consumer Service (ACS) URL", "acsURL", "acsUrl"), { wordBreak: true })}
                      {isSamlFlow && renderModalField("Audience URI (SP Entity ID)", getFieldValue("Audience URI (SP Entity ID)", "entityID", "audienceUri", "spEntityId"), { wordBreak: true })}
                      {isSamlFlow && renderModalField("Name ID Format", getFieldValue("Name ID Format", "nameIdFormat", "nameIdFormatType", "nameIDFormat"))}
                      {isSamlFlow && renderModalField("Application Short Name", getFieldValue("Application Short Name", "applicationShortName"))}
                      {isSamlFlow && renderModalField("Application Access Control", getFieldValue("Application Access Control", "accessControls"))}
                      {isSamlFlow && renderModalField("Elevated Permissions", isYesValue(getFieldValue("Is Admin App", "Is application designed for administrators with elevated permissions, with restricted access for standard users ?", "isAdminApp")) ? "Yes" : "No")}

                      {isOidcFlow && renderModalField("Application Type", getFieldValue("Application Type", "applicationType", "applicationTypeName"))}
                      {isOidcFlow && renderModalField("Application URL", getFieldValue("Application URL", "applicationURL", "applicationUrl"), { wordBreak: true })}
                      {isOidcFlow && renderModalField("Application Short Name", getFieldValue("Application Short Name", "applicationShortName"))}
                      {isOidcFlow && renderModalField("Login URL / Sign-in Redirect URIs", getFieldValue("Login URL", "Sign-in Redirect URIs", "signinUris"), { wordBreak: true })}
                      {isOidcFlow && renderModalField("Logout URL / Sign-out Redirect URIs", getFieldValue("Logout URL", "Sign-out Redirect URIs", "signoutUris"), { wordBreak: true })}
                      {isOidcFlow && renderModalField("Grants", getFieldValue("Grants", "grants"))}
                      {isOidcFlow && renderModalField("Application Access Control", getFieldValue("Application Access Control", "accessControls"))}
                      {isOidcFlow && renderModalField("Elevated Permissions", isYesValue(getFieldValue("Is Admin App", "Elevated Permissions", "isAdminApp")) ? "Yes" : "No")}

                      {(isM2MFlow || hasAccessTypeValue) && renderModalField("Application/Services Type", m2mAccessTypeValue)}
                      {(isM2MFlow || hasScopesValue) && renderModalField("Custom Scope Name", hasValue(m2mCustomScopeNameValue) ? m2mCustomScopeNameValue : "-")}
                      {isM2MFlow && renderModalField("Grants", getFieldValue("Grants", "grants"))}

                      {renderModalField("IDX Attribute Name", getFieldValue("IDX Attribute Name", "idxAttribute", "idx_attribute", "IDX Attribute"))}
                      {hasValue(getFieldValue("Is New IDX Attribute", "newIdxAttribute", "New IDX Attribute", "isNewIdxAttribute", "is_new_idx_attribute")) &&
                        renderModalField("Is New IDX Attribute", isYesValue(getFieldValue("Is New IDX Attribute", "newIdxAttribute", "New IDX Attribute", "isNewIdxAttribute", "is_new_idx_attribute")) ? "Yes" : "No")}
                      {renderModalField("Authorization Roles", getFieldValue("Roles", "roles"))}
                      {renderModalField("Role Idx Attribute", getFieldValue("Role IDX Attribute", "Role Idx Attribute", "roleIdxAttribute", "role_idx_attribute"))}
                      {hasValue(getFieldValue("Is New Role IDX Attribute", "newRoleIdxAttribute", "New Role IDX Attribute", "isNewRoleIdxAttribute", "is_new_role_idx_attribute")) &&
                        renderModalField("Is New Role IDX Attribute", isYesValue(getFieldValue("Is New Role IDX Attribute", "newRoleIdxAttribute", "New Role IDX Attribute", "isNewRoleIdxAttribute", "is_new_role_idx_attribute")) ? "Yes" : "No")}
                      {renderModalField("Comments", getFieldValue("Comments", "comments"))}
                      {hasValue(hasLogoRawValue) && renderModalField("Has Logo", hasLogo ? "Yes" : "No")}
                      {hasLogo && (
                        <div key="application-logo-preview" style={styles.detailsItem}>
                          <span style={styles.detailsItemLabel}>Application Logo</span>
                          <div style={{ width: "120px", height: "120px", border: "1px solid #e5e7eb", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", backgroundColor: "#f9fafb" }}>
                            {(ssoLogoUrl || fallbackLogoUrl) ? (
                              <img
                                src={ssoLogoUrl || fallbackLogoUrl}
                                alt="Application Logo"
                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", cursor: "zoom-in" }}
                                title="Click to enlarge"
                                onClick={() => setLogoPreviewSrc(ssoLogoUrl || fallbackLogoUrl)}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "";
                                  e.target.alt = "Logo not available";
                                }}
                              />
                            ) : (
                              <span style={{ fontSize: "12px", color: "#999" }}>Loading...</span>
                            )}
                          </div>
                        </div>
                      )}
                      {isSamlFlow && hasValue(hasCertificateRawValue) && renderModalField("Has Certificate", hasCertificate ? "Yes" : "No")}
                      {isSamlFlow && hasCertificate && (
                        <div key="certificate-download" style={styles.detailsItem}>
                          <span style={styles.detailsItemLabel}>Certificate</span>
                          {(ssoCertificateUrl || fallbackCertificateUrl) ? (
                            <a
                              href={ssoCertificateUrl || fallbackCertificateUrl}
                              download={certificateFileName}
                              title="Download Certificate"
                              style={{ display: "inline-flex", alignItems: "center", gap: "8px", cursor: "pointer", color: "#004895", textDecoration: "none", fontSize: "13px", fontWeight: 600 }}
                            >
                              <span>{certificateFileName}</span>
                              <span aria-hidden="true">Download</span>
                            </a>
                          ) : (
                            <span style={{ fontSize: "12px", color: "#999" }}>Loading...</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!!tokenValidation && <div style={styles.sectionDivider}></div>}

                {!!tokenValidation && (
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>Machine2Machine Token Validation Details</div>
                    <div style={styles.tokenPanel}>
                      <div style={styles.tokenRow}>
                        <span style={styles.detailsItemLabel}>Status:</span>
                        <span
                          style={{
                            ...styles.tokenBadge,
                            ...getStatusPillStyles(getTokenValidationStatusColor(tokenValidation?.status)),
                          }}
                        >
                          {getTokenValidationStatusText(tokenValidation?.status)}
                        </span>
                      </div>

                      {tokenValidation?.message && <div style={styles.tokenMsg}>{tokenValidation?.message}</div>}

                      {Array.isArray(tokenValidation?.data) && tokenValidation?.data?.length > 0 && (
                        <div style={styles.tokenTableWrap}>
                          <table style={styles.tokenTable}>
                            <thead>
                              <tr>
                                <th style={styles.tokenTableHead}>DATE</th>
                                <th style={styles.tokenTableHead}>COUNT</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tokenValidation?.data?.map((item, idx) => (
                                <tr key={`${item?.date || "na"}-${idx}`}>
                                  <td style={styles.tokenTableCell}>{item?.date || "-"}</td>
                                  <td style={styles.tokenTableCell}>{item?.count ?? "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      <div style={styles.tokenRow}>
                        <span style={styles.detailsItemLabel}>Automated Decision:</span>
                        <span
                          style={{
                            ...styles.tokenBadge,
                            ...getStatusPillStyles(getAutomatedDecisionColor(tokenValidation)),
                          }}
                        >
                          {getAutomatedDecisionFromTokenStatus(tokenValidation)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {businessInfoFields.length === 0 && applicationInfoFields.length === 0 && !tokenValidation && (
                  <div style={{ color: "#6b7280", fontSize: "13px" }}>No additional details are available for this request.</div>
                )}
              </div>
            }
          </div>
        </div>
      )}

      {logoPreviewSrc && (
        <div style={styles.modalOverlay} onClick={() => setLogoPreviewSrc(null)}>
          <div
            style={{ ...styles.modalCard, width: "min(720px, 100%)", maxHeight: "90vh" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#1f2937" }}>Application Logo</h3>
              <button style={styles.modalClose} onClick={() => setLogoPreviewSrc(null)} aria-label="Close logo preview">
                ×
              </button>
            </div>
            <div style={{ ...styles.modalBody, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img
                src={logoPreviewSrc}
                alt="Application Logo Preview"
                style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowApproval;
